import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button, Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Save } from "@mui/icons-material";
import { useWorkspace } from "hooks/queries/useWorkspace";
import { useView, ViewConfig } from "hooks/queries/useView";
import { useBatch } from "hooks/queries/useBatch";
import { useTags } from "hooks/queries/useTags";
import { MultiEditForm, MultiEditItemType } from "components/Form/MultiEditForm";
import { WorkspaceConfig } from "types/WorkspaceConfig";
import { addTag, removeTag } from "../Form/Controls/MultiEditTagFormControl";
import { buildSingleTagPayload } from "./buildTagDeltaPayload";

export type MultiDocumentEditProps = {
  documentIds: string[],
  onSuccess?: () => void,
  onCancel?: () => void,
};

export function MultiDocumentEdit({
  documentIds,
  onSuccess,
  onCancel,
}: MultiDocumentEditProps) {
  const { workspaceId, viewId } = useParams<{
    workspaceId: string;
    viewId: string;
  }>();
  const workspace = useWorkspace(workspaceId!);
  const workspaceConfig = workspace.data?.config as WorkspaceConfig;
  const connectorConfig = workspaceConfig.connectorConfig;
  const tagConfig = workspaceConfig.tagConfig;
  const viewConfig = useView(workspaceId!, viewId!).data as ViewConfig;
  const editProps = viewConfig.editFormConfig.properties;
  const enableTags = viewConfig.editFormConfig.enableTags;

  const tagsQuery = useTags({
    workspaceId: workspaceId!,
    workspaceConfig,
    enabled: !!tagConfig,
  });

  const baseProperties = workspaceConfig.propertiesSchema
    .filter((p) => editProps.includes(p.name))
    .slice();

  if (enableTags && tagConfig) {
    baseProperties.push(
      {
        name: addTag,
        displayName: "Tags (Add)",
      },
      {
        name: removeTag,
        displayName: "Tags (Remove)",
      }
    );
  }

  const [item, setItem] = useState<MultiEditItemType>();
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const batchMutation = useBatch(workspaceConfig);

  useEffect(() => {
    if (item?.property === removeTag) {
      if (!tagsQuery.data.includes(item.value as string)) {
        setIsSaveDisabled(true);
      }
      else {
        setIsSaveDisabled(false);
      }
    }
  }, [item]);

  const onSave = async () => {
    if (!item?.property || item.value === undefined) {
      return;
    }

    const isTagOp = (item.property === addTag || item.property === removeTag) && !!tagConfig;

    const requests = (documentIds || []).map((id) => {
      const payload: Record<string, any> = {};

      if (isTagOp) {
        const operationType = item.property === addTag ? "Add" : "Remove";
        payload[`${tagConfig.documentTagCollectionProperty}@delta`] = buildSingleTagPayload(
          item.value as string,
          id,
          tagConfig.tagDocumentIdProperty,
          tagConfig.tagValueProperty,
          operationType
        );
      } else {
        payload[getPropertyName(item.property)] = item.value;
      }

      return {
        method: "patch",
        url: `${connectorConfig.entity}/${id}`,
        body: payload,
      };
    });
    await batchMutation.mutateAsync(requests);
    onSuccess?.();
  };

  return (
    <Stack gap={1}>
      <MultiEditForm
        item={item}
        properties={baseProperties}
        tagOptions={tagsQuery.data}
        onChange={setItem}
      />

      <Stack direction="row" gap={1} justifyContent="end">
        <Button color="primary" variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          endIcon={<Save />}
          loading={batchMutation.isPending}
          loadingPosition="end"
          onClick={onSave}
          disabled={isSaveDisabled}
        >
          Save
        </LoadingButton>
      </Stack>
    </Stack>
  );

  // Maps semantic property keys to real connectorConfig fields
  function getPropertyName(property: string) {
    if (property === "name") {
      return connectorConfig.fileNameProperty;
    }
    if (property === "title") {
      return connectorConfig.titleProperty;
    }
    if (property === addTag || property === removeTag) {
      return tagConfig!.documentTagCollectionProperty;
    }
    return property;
  }
}